import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SessionStorageService } from '@services/storage.service';
import { ExternalUrlService, IUserExternalUrlRelation, IUserExternalUrlRelations } from '@shared/services/externalurl.service';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';
import { IUserAccess, UserService } from '../../../../..//shared/services/user.service';
import { IInsightsUserResource, InsightsService } from '../../../../../shared/services/insights.service';
import { IReport, IUserReportRelation, IUserReportRelations, ReportService } from '../../../../../shared/services/report.service';

@Component({
	selector: 'app-dialog-user-reports',
	templateUrl: './dialog-user-reports.component.html',
	styleUrls: ['./dialog-user-reports.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class DialogUserReportsComponent implements OnInit, OnDestroy {
	initialDefaultReport: string = '';
	selectedDefaultReport: string = '';
	unsubscribe: Subject<void> = new Subject<void>();
	permissions: string[];

	constructor(
		public dialogRef: MatDialogRef<DialogUserReportsComponent>,
		@Inject(MAT_DIALOG_DATA) public data: IUserAccess,
		public reportService: ReportService,
		public externalUrlService: ExternalUrlService,
		public userService: UserService,
		public insightsService: InsightsService,
		private sessionStorageService: SessionStorageService,
	) {}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.permissions = JSON.parse(this.sessionStorageService.get('User.Permissions'));
		combineLatest([this.reportService.fetchUserReportsById(this.data.id).pipe(take(1)), this.externalUrlService.fetchUserExternalUrlsById(this.data.id).pipe(take(1))]).subscribe((data) => {
			if (data[0] && data[1]) {
				this.userReports = [...data[0].result, ...data[1].result];
				combineLatest([this.reportService.fetchReports().pipe(take(1)), this.externalUrlService.fetchExternalUrls().pipe(take(1))]).subscribe((data) => {
					if (data[0] && data[1]) {
						data[0].result.forEach((item) => {
							if (!item.isDeleted && !this.userReports.find((x) => x.name === item.name)) {
								this.reports.push(item);
							}
							this.setInitialSelectedDefaultReport();
						});
						data[1].result.forEach((item) => {
							if (!item.isDeleted && !this.userReports.find((x) => x.name === item.name)) {
								this.reports.push(item);
							}
							this.setInitialSelectedDefaultReport();
						});
					}
				});
			}
		});
	}

	setInitialSelectedDefaultReport(): void {
		if (this.data.defaultViewType && this.data.defaultViewType.toLowerCase() === 'report') {
			if (this.userReports && this.userReports.length > 0) {
				const selectedReport = this.userReports.find((x) => x.externalId === this.data.defaultViewId)?.externalId;
				if (selectedReport?.length > 0) {
					this.selectedDefaultReport = selectedReport;
				} else {
					const selectedExtUrl = this.userReports.find((x) => x.externalUrlId === this.data.defaultViewId)?.externalUrlId;
					if (selectedExtUrl?.length > 0) {
						this.selectedDefaultReport = selectedExtUrl;
					}
				}
				this.initialDefaultReport = this.selectedDefaultReport ? this.selectedDefaultReport : '';
			}
		}
	}

	selectDefaultReport(report: IReport): void {
		if (report.externalUrlId?.length > 0) {
			this.setSelectedDefaultReport(report.externalUrlId);
		} else {
			this.setSelectedDefaultReport(report.externalId);
		}
	}

	private setSelectedDefaultReport(id: string): void {
		if (this.selectedDefaultReport === id) {
			this.selectedDefaultReport = '';
		} else {
			this.selectedDefaultReport = id;
		}
	}

	removeUserReport(report: IReport): void {
		const index = this.userReports.findIndex((x) => x.externalId === report.externalId);
		transferArrayItem(this.userReports, this.reports, index, this.reports.length);
		if (this.selectedDefaultReport === report.externalId) {
			this.selectedDefaultReport = '';
		}
	}

	saveUserDefaultReport() {
		const reportUserId = this.userService.impersonateUser ? this.userService.impersonateUser.id : null;
		const isAdmin = this.userService.impersonateUser ? false : this.permissions.findIndex((x) => x === 'insights:ReportCreate') > 0;
		if (this.initialDefaultReport !== this.selectedDefaultReport) {
			const userResource: IInsightsUserResource = {
				externalId: this.data.id,
				id: this.data.id,
				slideshowInterval: this.data.interval,
			};
			if (this.selectedDefaultReport !== '') {
				userResource.defaultViewId = this.selectedDefaultReport;
				userResource.defaultViewType = 'report';
			}
			this.insightsService
				.updateUser(userResource)
				.pipe(takeUntil(this.unsubscribe))
				.subscribe((x) => {
					console.log(`updated user: ${JSON.stringify(x)}`);
					this.reportService.fetchUserReports(reportUserId, isAdmin);
					this.dialogRef.close();
				});
		} else {
			this.reportService.fetchUserReports(reportUserId, isAdmin);
			this.dialogRef.close();
		}
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	saveUserReports() {
		const userReportRelations: IUserReportRelations = {
			userId: this.data.id,
			userReports: [],
		};
		const userExternalUrlRelations: IUserExternalUrlRelations = {
			userId: this.data.id,
			userExternalUrls: [],
		};
		this.userReports.forEach((item) => {
			if (item.groupId?.length > 0) {
				const userRelation: IUserReportRelation = {
					userId: this.data.id,
					reportId: item.reportId ? item.reportId : item.id,
				};
				userReportRelations.userReports.push(userRelation);
			} else {
				const userRelationExt: IUserExternalUrlRelation = {
					userId: this.data.id,
					externalUrlId: item.externalUrlId ? item.externalUrlId : item.id,
				};
				userExternalUrlRelations.userExternalUrls.push(userRelationExt);
			}
		});
		combineLatest([this.reportService.addUserReports(userReportRelations).pipe(take(1)), this.externalUrlService.addUserExternalUrls(userExternalUrlRelations)])
			.pipe(take(1))
			.subscribe((data) => {
				if (data[0] && data[1]) {
					console.log(`saving user reports: ${JSON.stringify(data[0])}`);
					console.log(`saving user externalurls: ${JSON.stringify(data[1])}`);
					this.saveUserDefaultReport();
				}
			});
	}

	userReports: IReport[] = [];

	reports: IReport[] = [];

	drop(event: CdkDragDrop<IReport[]>) {
		if (event.previousContainer === event.container) {
			moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			if (this.selectedDefaultReport === event.previousContainer.data[event.previousIndex].externalId) {
				this.selectedDefaultReport = '';
			}
			transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
		}
	}
}
